import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";

function DropdownValuesSetup({ dropdownName = null, isReadOnly = false }) {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      commonSettingId: "",
      key: "",
      value: "",
    },
  });

  const [dropdownNames, setDropdownNames] = useState([]);
  const [selectedDropdownName, setSelectedDropdownName] = useState(null);
  const [dropdownValues, setDropdownValues] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getDropdownNamesByOrgId = async () => {
    try {
      const response = await get(`CommonSettings/GetCommonSettingByAsync`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDropdownNames(response?.commonSetting);
    } catch (error) {
      console.error("Error fetching dropdowns:", error);
    }
  };

  useEffect(() => {
    const fetchDropdowns = async () => {
      await getDropdownNamesByOrgId();
    };
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (dropdownNames.length && dropdownName) {
      const selected = dropdownNames.find(
        (item) => item.moduleName === dropdownName
      );
      if (selected) {
        const selectedOption = {
          value: selected.id,
          label: selected.moduleName,
        };
        setValue("commonSettingId", selectedOption);
        setSelectedDropdownName(selected.moduleName);
      }
    }
  }, [dropdownNames, dropdownName]);

  const getDropdownValuesByDropdownName = async () => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${selectedDropdownName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDropdownValues(response?.commonSettingDetail);
    } catch (error) {
      console.error("Error fetching dropdowns:", error);
    }
  };

  useEffect(() => {
    if (selectedDropdownName) {
      getDropdownValuesByDropdownName();
    }
  }, [selectedDropdownName, triggerRerender]);

  const handleEdit = async (row) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailById?id=${row.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const singleItem = response?.commonSettingDetail;

      const dropdownNamesResponse = await get(
        `CommonSettings/GetCommonSettingByAsync`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setDropdownNames(dropdownNamesResponse?.commonSetting);

      const selectedDropdown = dropdownNames.find(
        (dropdown) => dropdown.id === singleItem.commonSettingId
      );

      reset({
        id: singleItem.id || "",
        commonSettingId: {
          value: selectedDropdown?.id || "",
          label: selectedDropdown?.moduleName || "",
        },
        key: singleItem.value || "",
        value: singleItem.value || "",
      });
    } catch (error) {
      console.error("Error editing:", error);
    }
  };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      // width: "60px",
    },

    {
      name: "Value",
      selector: (row) => row.value,
      sortable: true,
      // width: "100px",
    },
    // {
    //   name: "Key",
    //   selector: (row) => row.key,
    //   sortable: true,
    //   // width: "250px",
    // },
    {
      name: "Action",
      // width: "60px",
      selector: (row) => (
        <div>
          <button
            type="button"
            className="btn p-0"
            onClick={() => handleEdit(row)}
          >
            <i className="fas fa-pencil"></i>
          </button>
        </div>
      ),
    },
  ];

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      ...data,
      commonSettingId: data?.commonSettingId?.value,
      key: data?.value,
    };

    try {
      const response = await post(
        "CommonSettings/AddUpdateCommonSettingDetail",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset((formValues) => ({
          ...formValues,
          id: 0,
          key: "",
          value: "",
        }));
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <div className="">
      {dropdownName ? (
        ""
      ) : (
        <div className="page-header d-flex mb-2">
          <h2 className="page-heading fs-7">Dropdown Values</h2>
        </div>
      )}

      <div className="row g-3">
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-body">
              <form className="row g-3" onSubmit={handleSubmit(saveData)}>
                <div className="col-md-12">
                  <label className="form-label">
                    Dropdown Name<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="commonSettingId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={dropdownNames.map((dropdown) => ({
                          value: dropdown.id,
                          label: dropdown.moduleName,
                        }))}
                        isDisabled={isReadOnly}
                        onChange={(selectedOption, actionMeta) => {
                          if (!isReadOnly) {
                            setValue("commonSettingId", selectedOption);
                            setSelectedDropdownName(selectedOption?.label);
                            trigger("commonSettingId");
                          }
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable={!isReadOnly}
                      />
                    )}
                  />

                  {errors.commonSettingId && (
                    <small className="text-danger">
                      {errors.commonSettingId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      Value<span className="text-danger fs-9">*</span>
                    </div>
                  </label>
                  <input
                    {...register(`value`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    onChange={handleChangeOfInputWithLengthCheck("value")}
                  />
                  {errors.value && (
                    <small className="text-danger">
                      {errors.value.message}
                    </small>
                  )}
                </div>
                {/* <div className="col-md-4">
                  <label className="form-label">
                    <div className="d-flex align-items-center">
                      Key<span className="text-danger fs-9">*</span>
                    </div>
                  </label>
                  <input
                    {...register(`key`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 250,
                        message: "Maximum of 250 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    onChange={handleChangeOfInputWithLengthCheck("key")}
                  />
                  {errors.key && (
                    <small className="text-danger">{errors.key.message}</small>
                  )}
                </div> */}
                <div className="col-12 mt-3">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-8">
          <div className="card h-100">
            <div className="card-body">
              <DataTable
                columns={columns}
                data={dropdownValues}
                pagination
                dense
                customStyles={customTableStyles}
                highlightOnHover={true}
                onChangePage={setCurrentPage}
                onChangeRowsPerPage={setRowsPerPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DropdownValuesSetup;
